import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Toggle } from "@fp/ui/components/base/toggle/toggle";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { Input } from "@fp/ui/components/base/input/input";
import { PhoneInput } from "@fp/ui/components/base/input/input-phone";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "@/lib/axios";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import {
  useGetSettings,
  useUpdateSettings,
  getGetSettingsQueryKey,
} from "@fp/admin-api-client/settings/settings.client";
import type { UpdateAdminNotificationSettingsDto } from "@fp/admin-api-client/types/updateAdminNotificationSettingsDto";

interface UserProfileDto {
  id: string;
  primaryEmail: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

interface UpdateProfileDto {
  firstName: string;
  lastName: string;
  phone?: string;
}

interface Passkey {
  id: string;
  name: string | null;
  createdDate: string;
  lastUsedDate: string;
}

export const Route = createFileRoute("/profile/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

async function fetchPasskeys(): Promise<Passkey[]> {
  const response = await axios.get<Passkey[]>("/api/auth/passkey");
  return response.data;
}

async function deletePasskey(id: string): Promise<void> {
  await axios.delete(`/api/auth/passkey/${id}`);
}

function RouteComponent() {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteModalPasskey, setDeleteModalPasskey] = useState<{ id: string; name: string } | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");


  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const response = await axios.get<UserProfileDto>("/api/users/me");
      return response.data;
    },
  });

  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName ?? "");
      setLastName(userProfile.lastName ?? "");
      setPhone(userProfile.phone ?? "");
    }
  }, [userProfile]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: UpdateProfileDto) => {
      const response = await axios.patch<UserProfileDto>("/api/users/me", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success("Profile updated successfully");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Failed to update profile");
    },
  });

  const handleUpdateProfile = () => {
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!lastName.trim()) {
      toast.error("Last name is required");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length !== 10) {
      toast.error("Phone number must be 10 digits");
      return;
    }
    updateProfileMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phoneDigits || undefined,
    });
  };

  const { data: passkeys, isLoading } = useQuery({
    queryKey: ["passkeys"],
    queryFn: fetchPasskeys,
  });

  const { data: settingsResponse, isLoading: isLoadingNotificationSettings } = useGetSettings();
  const notificationSettings = settingsResponse?.data?.notifyBy;

  const updateSettingsMutation = useUpdateSettings({
    mutation: {
      onMutate: async (newData) => {
        await queryClient.cancelQueries({ queryKey: getGetSettingsQueryKey() });
        const previousSettings = queryClient.getQueryData(getGetSettingsQueryKey());
        queryClient.setQueryData(getGetSettingsQueryKey(), (old: any) => ({
          ...old,
          data: { ...old?.data, notifyBy: newData.data.notifyBy },
        }));
        return { previousSettings };
      },
      onError: (_err, _newData, context) => {
        queryClient.setQueryData(getGetSettingsQueryKey(), context?.previousSettings);
        toast.error("Failed to update notification preferences");
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
      },
    },
  });

  const handleNotificationToggle = (field: keyof UpdateAdminNotificationSettingsDto, value: boolean) => {
    if (!notificationSettings) return;
    const { inbox: _inbox, ...updatableSettings } = notificationSettings;
    updateSettingsMutation.mutate({
      data: { notifyBy: { ...updatableSettings, [field]: value } },
    });
  };

  const deleteMutation = useMutation({
    mutationFn: deletePasskey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["passkeys"] });
      setDeletingId(null);
    },
    onError: () => {
      setDeletingId(null);
      toast.error("Failed to delete passkey. Please try again.");
    },
  });

  const handleDelete = (id: string, name: string | null) => {
    setDeleteModalPasskey({ id, name: name || "this passkey" });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalPasskey) {
      setDeletingId(deleteModalPasskey.id);
      deleteMutation.mutate(deleteModalPasskey.id);
      setDeleteModalPasskey(null);
    }
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Profile"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer>
        <div className="space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-4">Your Information</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
              {isLoadingProfile ? (
                <p className="text-gray-600">Loading profile...</p>
              ) : (
                <>
                  <div className="text-sm text-gray-600">
                    Email:{" "}
                    <span className="font-medium text-gray-900">
                      {userProfile?.primaryEmail}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="First Name"
                      value={firstName}
                      onChange={setFirstName}
                      isRequired
                    />
                    <Input
                      label="Last Name"
                      value={lastName}
                      onChange={setLastName}
                      isRequired
                    />
                  </div>
                  <PhoneInput
                    label="Phone (optional)"
                    value={phone}
                    onChange={setPhone}
                  />
                  <Button
                    onClick={handleUpdateProfile}
                    color="primary"
                    isLoading={updateProfileMutation.isPending}
                    isDisabled={updateProfileMutation.isPending}
                  >
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Passkeys</h2>
            {isLoading ? (
              <p className="text-gray-600">Loading passkeys...</p>
            ) : passkeys && passkeys.length > 0 ? (
              <div className="space-y-3">
                {passkeys.map((passkey) => (
                  <div
                    key={passkey.id}
                    className="p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="font-medium">
                        {passkey.name || "Unnamed Passkey"}
                      </div>
                      <Button
                        onClick={() => handleDelete(passkey.id, passkey.name)}
                        color="secondary"
                        disabled={deletingId === passkey.id}
                      >
                        {deletingId === passkey.id ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Created: {formatLocalDate(passkey.createdDate, "dateWithYear")} • Last used:{" "}
                      {formatLocalDate(passkey.lastUsedDate, "dateWithYear")}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">
                No passkeys registered. You can add a passkey during your next
                sign-in.
              </p>
            )}
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
              {isLoadingNotificationSettings ? (
                <p className="text-gray-600">Loading notification preferences...</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Turning a channel off stops every notification on it. Notifications always
                    arrive in your notification center regardless.
                  </p>
                  <Toggle
                    label="Email"
                    hint="Receive notifications via email"
                    isSelected={notificationSettings?.email ?? false}
                    onChange={(value: boolean) => handleNotificationToggle("email", value)}
                  />
                  <Toggle
                    label="Push"
                    hint="Receive push notifications on your devices"
                    isSelected={notificationSettings?.push ?? false}
                    onChange={(value: boolean) => handleNotificationToggle("push", value)}
                  />
                  <Toggle
                    label="In-app"
                    hint="Receive real-time notifications while you're signed in"
                    isSelected={notificationSettings?.signalR ?? false}
                    onChange={(value: boolean) => handleNotificationToggle("signalR", value)}
                  />
                  <div className="pt-2 border-t border-gray-200">
                    <Link
                      to="/profile/notifications"
                      className="text-sm font-medium text-brand-600 hover:text-brand-700"
                    >
                      Choose which notifications you receive →
                    </Link>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </PageContentContainer>
      <ConfirmationModal
        isOpen={!!deleteModalPasskey}
        onClose={() => setDeleteModalPasskey(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Passkey"
        message={`Are you sure you want to delete ${deleteModalPasskey?.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={!!deletingId}
      />
    </AdminLayout>
  );
}
